import React from 'react';
import { Shield, Lock, Eye, Database, UserCheck, Globe } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="text-center mb-12">
            <Shield className="w-16 h-16 text-amber-600 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600 text-lg">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              At Rushmore Publishing, we are committed to protecting your privacy and handling your personal information with care and transparency. This Privacy Policy explains how we collect, use, store, and protect your information when you visit our website or use our services.
            </p>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Information We Collect</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Submit inquiries through our contact forms</li>
                <li>Request quotes or consultations for our publishing services</li>
                <li>Subscribe to our newsletter or updates</li>
                <li>Create an account or engage our services</li>
                <li>Communicate with us via email, phone, or chat</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                This information may include your name, email address, phone number, mailing address, project details, and payment information.
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">How We Use Your Information</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>To provide, maintain, and improve our publishing services</li>
                <li>To communicate with you about your projects, orders, and inquiries</li>
                <li>To process payments and fulfill service agreements</li>
                <li>To send you marketing communications (with your consent)</li>
                <li>To personalize your experience on our website</li>
                <li>To analyze website usage and improve our offerings</li>
                <li>To comply with legal obligations and protect our rights</li>
              </ul>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Data Security</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">
                We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. Your data is stored on secure servers with restricted access limited to authorized personnel who require it to perform their job functions. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Cookies and Tracking Technologies</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our website uses cookies and similar tracking technologies to enhance your browsing experience. Cookies are small text files stored on your device that help us:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Remember your preferences and settings</li>
                <li>Understand how you interact with our website</li>
                <li>Analyze website traffic and performance</li>
                <li>Deliver relevant content and advertisements</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can control cookie preferences through your browser settings, though disabling cookies may affect website functionality.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Processing</h2>
              <p className="text-gray-700 leading-relaxed">
                We work with trusted third-party payment processors to handle transactions securely. We do not store your complete credit card information on our servers. Payment information is encrypted and processed in accordance with Payment Card Industry Data Security Standards (PCI DSS).
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                We may share your information with trusted third-party service providers who assist us in operating our website, conducting our business, or servicing you. These providers are contractually obligated to keep your information confidential and use it only for the purposes we specify. We do not sell, rent, or trade your personal information to third parties for marketing purposes.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">SMS and Communication Consent</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you opt in to receive text messages from us, we may use your phone number to send service updates, appointment reminders, and promotional offers. By providing your phone number, you consent to receive these communications. You can opt out at any time by replying STOP to any message. Standard message and data rates may apply.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We do not share your mobile information with third parties for their marketing purposes. However, we may share your data with service providers who help us deliver text messaging services, such as SMS platform providers and telecommunications carriers.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property and Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed">
                We understand the importance of confidentiality in publishing. All manuscripts, creative content, and project materials you share with us are treated as confidential. We implement strict confidentiality protocols, and our team members sign non-disclosure agreements. You retain full ownership and copyright of all materials you provide and the work we create for you.
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <UserCheck className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Your Rights</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request corrections to inaccurate or incomplete information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to or restrict certain types of data processing</li>
                <li>Withdraw consent for marketing communications at any time</li>
                <li>Request a copy of your data in a portable format</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child without parental consent, we will take steps to delete that information promptly.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                If you are accessing our services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States or other countries where we or our service providers operate. By using our services, you consent to such transfers.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website with a new effective date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
              </p>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="text-gray-700 space-y-2">
                <p><strong>Rushmore Publishing</strong></p>
                <p>Email: privacy@rushmorepublishing.com</p>
                <p>Phone: +1 (866) 978-8498</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;